import { Injectable } from '@angular/core';
import { Device } from '@capacitor/device';
import { CapacitorSQLite, CapacitorSQLitePlugin, SQLiteConnection, SQLiteDBConnection, capSQLiteChanges, capSQLiteSet } from '@capacitor-community/sqlite';
import { Capacitor } from '@capacitor/core';

/**
 * Classe che rappresenta l'oggetto risultato
 * ritornato dall'esecuzione di una query sul DB
 * attraverso il plugin
 */
export interface SqlResultSet {
  insertId;
  /**
   * Array di rows iterabile
   * - Esiste un campo values.lenth che da la lunghezza dell'elenco dei record
   * - E' possibile ottenere il contenuto del singolo record
   * tramite values[k] dove k è l'indice dell'elemento oppure
   * values[k].columnName
   */
  rows: {
    length: number;
    item(index: number): any;
  };
  rowsAffected: number;
}


@Injectable({
  providedIn: 'root'
})
export class SqlAdapterService {
  private readonly DB_NAME = 'App';

  database: SQLiteDBConnection;
  sqliteConnection: SQLiteConnection;
  isAvailableOnPlatform = false;
  private platform: string;

  constructor() { }

  /**
   * Plugin Initialization
   */
  async initializePlugin(): Promise<void> {
    if (!this.isAvailableOnPlatform) {
      const info = await Device.getInfo();
      this.platform = info.platform;
      if (this.platform === 'ios' || this.platform === 'android') {
        this.isAvailableOnPlatform = true;
      } else { // NON ATTIVO
        this.isAvailableOnPlatform = false;
      }

      if (this.isAvailableOnPlatform) {
        this.sqliteConnection = new SQLiteConnection(CapacitorSQLite);
        this.database = await this.sqliteConnection.createConnection(this.DB_NAME,false, 'no-encryption', 1,false);
        await this.database.open();
        console.log('DB SQL opened correctly');
      }
    } else { // Already initialized
      return Promise.resolve();
    }
  }

  /**
   * Execute the given sql statement
   * @param statements 
   */
  async execute(statement: SqlStatement): Promise<capSQLiteSet> {
    if (this.isAvailableOnPlatform && statement) {
      const values = statement.values ? statement.values : [];
      return await this.database.query(statement.statement, values);
    } else {
      console.warn('DB SQLite not available');
      return Promise.reject('DB SQLite not available');
    }
  }

  /**
   * Execute a set of Raw Statements
   * @param statements as string ; separated
   */
  async executeSet(statements: SqlStatement[]): Promise<capSQLiteChanges> {
    if (this.isAvailableOnPlatform && statements && statements.length > 0) {
      const batchStatements = statements.map(statement => ({ statement: statement.statement, values: statement.values || [] }));
      return await this.database.executeSet(batchStatements);
    } else {
      console.warn('DB SQLite not available');
      return Promise.reject('DB SQLite not available');
    }
  }

  /**
   * Close the Database
   * @param dbName string
   */
  async close(): Promise<void> {
    if (this.isAvailableOnPlatform) {
      await this.database.close();
      await this.sqliteConnection.closeConnection(this.DB_NAME,false);
    } else {
      console.warn('DB SQLite not available');
      return Promise.reject('DB SQLite not available');
    }
  }

  /**
   * Delete the Database file
   * @param dbName string
   */
  async deleteDB(): Promise<void> {
    if (this.isAvailableOnPlatform) {
      await this.sqliteConnection.closeConnection(this.DB_NAME,false);
      await CapacitorSQLite.deleteDatabase({ database: this.DB_NAME });
    } else {
      console.warn('DB SQLite not available');
      return Promise.reject('DB SQLite not available');
    }
  }
}


export interface SqlStatement {
  statement: string;
  values?: Array<any>;
}